import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Icon } from '../Icon'
import { Tooltip, suppressTooltipsBriefly } from '../Tooltip'
import { boundsFor } from '../../../layout/segments'
import { cx } from '../cx'
import './nav.css'

export interface FabAction { id: string; to: string; label: string; icon: string }

/**
 * FAB menu M3 Expressive: pulsante "+" che apre l'elenco delle azioni rapide (link a rotte esistenti).
 * - `placement`: floating (in basso a destra, apre verso l'alto), rail (FAB nello slot del rail compresso, apre a lato)
 *   o drawer (FAB esteso "Nuovo" in testa all'expanded rail, apre verso il basso).
 * - Tastiera: Invio/Spazio/Freccia giu' aprono col focus sulla prima azione, Freccia su sull'ultima; Frecce e Home/End
 *   si muovono nell'elenco; Esc chiude e riporta il focus sul FAB; Tab chiude; clic fuori e scelta chiudono.
 */
export function FabMenu({
  actions, placement = 'floating', label = 'Nuovo', className,
}: {
  actions: FabAction[]
  placement?: 'floating' | 'rail' | 'drawer'
  label?: string
  className?: string
}) {
  const [open, setOpen] = useState(false)
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null)
  const btnRef = useRef<HTMLButtonElement | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)
  const wantFocus = useRef<'first' | 'last' | null>(null)
  const location = useLocation()
  const id = useId()
  const extended = placement === 'drawer'

  useEffect(() => { setOpen(false) }, [location.pathname, location.search])

  // Posizione: nel segmento del FAB, mai a cavallo della cerniera.
  useLayoutEffect(() => {
    if (!open) { setPos(null); return }
    const btn = btnRef.current
    const list = listRef.current
    if (!btn || !list) return
    const r = btn.getBoundingClientRect()
    const m = list.getBoundingClientRect()
    const bd = boundsFor(r)
    let left: number
    let top: number
    if (placement === 'floating') {
      left = r.right - m.width
      top = r.top - 8 - m.height
    } else if (placement === 'rail') {
      left = r.right + 8
      top = r.top
    } else {
      left = r.left
      top = r.bottom + 8
    }
    left = Math.min(Math.max(bd.left + 8, left), Math.max(bd.left + 8, bd.right - m.width - 8))
    top = Math.min(Math.max(bd.top + 8, top), Math.max(bd.top + 8, bd.bottom - m.height - 8))
    setPos({ left, top })
  }, [open, placement])

  useEffect(() => {
    if (!open || !pos || !wantFocus.current) return
    const items = listRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]')
    if (!items || items.length === 0) return
    ;(wantFocus.current === 'last' ? items[items.length - 1] : items[0])?.focus()
    wantFocus.current = null
  }, [open, pos])

  useEffect(() => {
    if (!open) return undefined
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node
      if (listRef.current?.contains(t) || btnRef.current?.contains(t)) return
      setOpen(false)
    }
    const onResize = () => setOpen(false)
    document.addEventListener('pointerdown', onDown, true)
    window.addEventListener('resize', onResize)
    return () => { document.removeEventListener('pointerdown', onDown, true); window.removeEventListener('resize', onResize) }
  }, [open])

  if (actions.length === 0) return null

  const close = (focusBack: boolean) => {
    setOpen(false)
    if (focusBack) { suppressTooltipsBriefly(); btnRef.current?.focus() }
  }
  const onButtonKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      wantFocus.current = e.key === 'ArrowUp' ? 'last' : 'first'
      setOpen(true)
    } else if (e.key === 'Escape' && open) { e.preventDefault(); close(false) }
  }
  const onMenuKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(e.currentTarget.querySelectorAll<HTMLElement>('[role="menuitem"]'))
    const cur = items.indexOf(document.activeElement as HTMLElement)
    const go = (i: number) => { e.preventDefault(); items[(i + items.length) % items.length]?.focus() }
    if (e.key === 'ArrowDown') go(cur + 1)
    else if (e.key === 'ArrowUp') go(cur < 0 ? items.length - 1 : cur - 1)
    else if (e.key === 'Home') go(0)
    else if (e.key === 'End') go(items.length - 1)
    else if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(true) }
    else if (e.key === 'Tab') close(false)
  }
  const onToggle = () => {
    if (!open) wantFocus.current = 'first'
    setOpen(o => !o)
  }

  const button = (
    <button
      ref={btnRef}
      type="button"
      className={cx('md-fab', extended && !open && 'md-fab--extended', open && 'md-fab--open', 'md-state')}
      aria-haspopup="menu"
      aria-expanded={open}
      aria-controls={open ? id : undefined}
      aria-label={extended && !open ? `${label} – azioni rapide` : 'Azioni rapide'}
      onClick={onToggle}
      onKeyDown={onButtonKey}
    >
      <Icon name={open ? 'close' : 'add'} size={open ? 20 : 24} />
      {extended && !open ? <span className="md-fab__label">{label}</span> : null}
    </button>
  )

  return (
    <div className={cx('md-fab-menu', `md-fab-menu--${placement}`, className)}>
      <Tooltip label="Azioni rapide" describe={false} placement={placement === 'floating' ? 'top' : 'bottom'}>{button}</Tooltip>
      {open ? (
        <div
          ref={listRef}
          id={id}
          role="menu"
          aria-label="Azioni rapide"
          className="md-fab-menu__list"
          style={pos ? { left: pos.left, top: pos.top } : { left: 0, top: 0, visibility: 'hidden' }}
          onKeyDown={onMenuKey}
        >
          {actions.map(a => (
            <Link key={a.id} to={a.to} role="menuitem" className="md-fab-menu__item md-state" onClick={() => setOpen(false)}>
              <Icon name={a.icon} size={24} />
              <span>{a.label}</span>
            </Link>
          ))}
        </div>
      ) : null}
    </div>
  )
}
